import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Download, CheckCircle, AlertTriangle, Filter } from 'lucide-react'
import { accountingApi, type BalanceLine } from '@/modules/accounting/api/accounting.api'
import { Link } from 'react-router-dom'

const formatXOF = (n: number) =>
  n === 0 ? '—' : new Intl.NumberFormat('fr-SN', { minimumFractionDigits: 0 }).format(Math.round(n))

const CLASS_LABELS: Record<number, string> = {
  1: 'Ressources durables',   2: 'Emplois stables',
  3: 'Stocks',                4: 'Tiers',
  5: 'Trésorerie',            6: 'Charges',
  7: 'Produits',              8: 'Autres',
  9: 'Analytique',
}

const CLASS_COLORS: Record<number, string> = {
  1: 'text-[#7C3AED]', 2: 'text-[var(--blue-d)]', 3: 'text-[#0E7490]',
  4: 'text-[#D97706]',  5: 'text-[#059669]', 6: 'text-[#DC2626]',
  7: 'text-[#059669]',  8: 'text-[var(--tx3)]',   9: 'text-[#DB2777]',
}

// ─── Export CSV ───────────────────────────────────────────────────────────────

function exportCsv(lines: BalanceLine[], fyLabel: string) {
  const header = 'Code;Libellé;Classe;Total Débit;Total Crédit;Solde Débiteur;Solde Créditeur\n'
  const rows = lines.map(l =>
    `${l.accountCode};${l.accountName};${l.class};${l.totalDebit};${l.totalCredit};${l.balanceDebit};${l.balanceCredit}`
  ).join('\n')
  const blob = new Blob(['\uFEFF' + header + rows], { type: 'text/csv;charset=utf-8' })
  const url  = URL.createObjectURL(blob)
  const a    = Object.assign(document.createElement('a'), { href: url, download: `balance-${fyLabel}.csv` })
  a.click()
  URL.revokeObjectURL(url)
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function TrialBalancePage() {
  const [fiscalYearId, setFY]     = useState('')
  const [classFilter, setClass]   = useState<number | ''>('')
  const [expanded, setExpanded]   = useState<Set<number>>(new Set([1,2,3,4,5,6,7]))

  const { data: fiscalYears = [] } = useQuery({
    queryKey: ['fiscal-years'],
    queryFn:  accountingApi.getFiscalYears,
  })

  const currentFY = fiscalYears.find(f => f.status === 'OPEN') ?? fiscalYears[0]
  const activeFY  = fiscalYearId || currentFY?.id

  const { data: balance, isLoading } = useQuery({
    queryKey: ['trial-balance', activeFY, classFilter],
    queryFn:  () => accountingApi.getTrialBalance(activeFY!, {
      classFilter: classFilter ? String(classFilter) : undefined,
    }),
    enabled: !!activeFY,
  })

  const fyLabel = fiscalYears.find(f => f.id === activeFY)?.label ?? ''

  // Grouper par classe
  const grouped = (balance?.lines ?? []).reduce<Record<number, BalanceLine[]>>((acc, l) => {
    if (!acc[l.class]) acc[l.class] = []
    acc[l.class].push(l)
    return acc
  }, {})

  const toggleClass = (cls: number) =>
    setExpanded(s => { const n = new Set(s); n.has(cls) ? n.delete(cls) : n.add(cls); return n })

  return (
    <div className="space-y-5 nx-fade-up">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Balance Générale</h1>
          <p className="text-sm text-[var(--tx3)] mt-0.5">SYSCOHADA 2018 · Vérification d'équilibre</p>
        </div>
        <div className="flex items-center gap-2">
          {balance && (
            <button
              onClick={() => exportCsv(balance.lines, fyLabel)}
              className="flex items-center gap-2 px-3 py-2 border border-[var(--bd)] text-[var(--tx3)] hover:text-[var(--brand-dark)] text-sm rounded-lg transition-colors"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          )}
        </div>
      </div>

      {/* Filtres */}
      <div className="flex items-center gap-3 flex-wrap">
        <select
          value={fiscalYearId}
          onChange={e => setFY(e.target.value)}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
        >
          {fiscalYears.map(fy => (
            <option key={fy.id} value={fy.id}>{fy.label}</option>
          ))}
        </select>
        <select
          value={classFilter}
          onChange={e => setClass(e.target.value ? Number(e.target.value) : '')}
          className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg px-3 py-2 text-sm text-[var(--brand-dark)] focus:outline-none focus:border-blue-500"
        >
          <option value="">Toutes les classes</option>
          {[1,2,3,4,5,6,7,8].map(c => <option key={c} value={c}>Classe {c}</option>)}
        </select>
      </div>

      {/* Statut équilibre */}
      {balance && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl border text-sm ${
          balance.isBalanced
            ? 'bg-[#10B981]/10 border-[rgba(16,185,129,0.25)] text-[#059669]'
            : 'bg-[#FEF2F2] border-[rgba(239,68,68,0.25)] text-[#DC2626]'
        }`}>
          {balance.isBalanced
            ? <CheckCircle className="w-4 h-4 shrink-0" />
            : <AlertTriangle className="w-4 h-4 shrink-0" />
          }
          <span>
            {balance.isBalanced
              ? `Balance équilibrée · Σ Débits = Σ Crédits = ${formatXOF(balance.totalDebit)} XOF`
              : `⚠️ Balance NON équilibrée — Écart : ${formatXOF(Math.abs(balance.totalDebit - balance.totalCredit))} XOF`
            }
          </span>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="space-y-2">
          {[1,2,3,4].map(i => <div key={i} className="h-10 bg-[var(--surface-2)] rounded-xl animate-pulse" />)}
        </div>
      ) : !balance ? (
        <div className="text-center py-10 text-[var(--tx3)]">Sélectionnez un exercice fiscal</div>
      ) : (
        <div className="bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl overflow-hidden">
          {/* En-têtes colonnes */}
          <div className="grid grid-cols-[2fr_3fr_1fr_1fr_1fr_1fr] gap-0 px-4 py-2.5 border-b border-[var(--bd)] text-xs font-medium text-[var(--tx3)] uppercase tracking-wide">
            <span>Code</span>
            <span>Libellé</span>
            <span className="text-right">Σ Débit</span>
            <span className="text-right">Σ Crédit</span>
            <span className="text-right">Sol. Déb.</span>
            <span className="text-right">Sol. Cré.</span>
          </div>

          {/* Lignes par classe */}
          {Object.entries(grouped)
            .sort(([a], [b]) => Number(a) - Number(b))
            .map(([cls, lines]) => {
              const clsNum   = Number(cls)
              const isExp    = expanded.has(clsNum)
              const color    = CLASS_COLORS[clsNum] ?? 'text-[var(--tx3)]'
              const subtotalD = lines.reduce((s, l) => s + l.totalDebit,   0)
              const subtotalC = lines.reduce((s, l) => s + l.totalCredit,  0)
              const subtotalBD = lines.reduce((s, l) => s + l.balanceDebit,  0)
              const subtotalBC = lines.reduce((s, l) => s + l.balanceCredit, 0)

              return (
                <div key={cls} className="border-b border-[var(--bd)] last:border-0">
                  {/* Ligne classe */}
                  <button
                    onClick={() => toggleClass(clsNum)}
                    className="w-full grid grid-cols-[2fr_3fr_1fr_1fr_1fr_1fr] gap-0 px-4 py-2.5 hover:bg-[#F4F2FB] transition-colors"
                  >
                    <span className={`text-xs font-bold ${color}`}>Classe {cls}</span>
                    <span className="text-xs text-[var(--tx3)] text-left">{CLASS_LABELS[clsNum]}</span>
                    <span className={`text-xs font-semibold text-right ${color}`}>{formatXOF(subtotalD)}</span>
                    <span className={`text-xs font-semibold text-right ${color}`}>{formatXOF(subtotalC)}</span>
                    <span className={`text-xs font-semibold text-right ${color}`}>{formatXOF(subtotalBD)}</span>
                    <span className={`text-xs font-semibold text-right ${color}`}>{formatXOF(subtotalBC)}</span>
                  </button>

                  {/* Lignes détail */}
                  {isExp && (
                    <div className="bg-[var(--bd)] border-t border-[var(--bd)] divide-y divide-[var(--bd)]">
                      {lines.map(line => (
                        <div
                          key={line.accountCode}
                          className="grid grid-cols-[2fr_3fr_1fr_1fr_1fr_1fr] gap-0 px-4 py-2 text-xs"
                        >
                          <Link
                            to={`/accounting/ledger?account=${line.accountCode}&fiscalYearId=${activeFY}`}
                            className="font-mono text-[var(--blue-d)] hover:text-blue-300 hover:underline"
                          >
                            {line.accountCode}
                          </Link>
                          <span className="text-[var(--tx2)] truncate pr-4">{line.accountName}</span>
                          <span className="text-right text-[var(--tx2)]">{formatXOF(line.totalDebit)}</span>
                          <span className="text-right text-[var(--tx2)]">{formatXOF(line.totalCredit)}</span>
                          <span className="text-right text-blue-300 font-medium">{formatXOF(line.balanceDebit)}</span>
                          <span className="text-right text-emerald-300 font-medium">{formatXOF(line.balanceCredit)}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            })}

          {/* Totaux généraux */}
          <div className={`grid grid-cols-[2fr_3fr_1fr_1fr_1fr_1fr] gap-0 px-4 py-3 border-t-2 ${balance.isBalanced ? 'border-emerald-500/30 bg-[#10B981]/5' : 'border-[rgba(239,68,68,0.35)] bg-red-500/5'}`}>
            <span className="text-xs font-bold text-[var(--brand-dark)] col-span-2">TOTAUX GÉNÉRAUX</span>
            <span className={`text-xs font-bold text-right ${balance.isBalanced ? 'text-[#059669]' : 'text-[#DC2626]'}`}>{formatXOF(balance.totalDebit)}</span>
            <span className={`text-xs font-bold text-right ${balance.isBalanced ? 'text-[#059669]' : 'text-[#DC2626]'}`}>{formatXOF(balance.totalCredit)}</span>
            <span className="text-xs font-bold text-right text-[var(--blue-d)]">{formatXOF((balance.lines ?? []).reduce((s, l) => s + l.balanceDebit,  0))}</span>
            <span className="text-xs font-bold text-right text-[#059669]">{formatXOF((balance.lines ?? []).reduce((s, l) => s + l.balanceCredit, 0))}</span>
          </div>
        </div>
      )}
    </div>
  )
}
